import { Resource } from "../../../AppDataProvider/types";
import { ResourceGroup } from "../../../GeneratedGraphQL";

export type GroupedResources = {
  groupId: string | null;
  groupName: string | null;
  resources: Resource[];
};

/**
 * Helper function to sort a list of Resources by ResourceGroup. For use
 * in variants of the ResourcesStep that require resource group display or
 * selection.
 *
 * @param resources An array of Resources to be sorted by group
 * @returns An array of GroupedResources objects, which contain the groupId and
 *          groupName, along with a list of Resources which belong to that group
 */
export const sortResourcesByGroup = (
  resources: Resource[],
): GroupedResources[] => {
  const groupedResources = resources.reduce(
    (result: GroupedResources[], resource: Resource) => {
      const addResourceToGroup = (group: ResourceGroup | null) => {
        const existingEntry = result.find(
          (entry) => entry.groupId === (group?._id ?? null),
        );

        existingEntry !== undefined
          ? existingEntry.resources.push(resource)
          : result.push({
              groupId: group?._id ?? null,
              groupName: group?.name ?? null,
              resources: [resource],
            });
      };

      const groups = resource.groups.edges.map(({ node }) => node);

      if (groups.length === 0) addResourceToGroup(null);
      groups.forEach((group) => {
        addResourceToGroup(group);
      });

      return result;
    },
    [],
  );

  groupedResources.sort((a, b) =>
    a.groupName === null
      ? 1
      : b.groupName === null
      ? -1
      : a.groupName.localeCompare(b.groupName),
  );

  return groupedResources;
};

export default sortResourcesByGroup;
